import type { CategoryItem, AlgoliaEventItem } from '../../types'
import type { GalleryItem } from '~/composables/types/event'
import type { GTMAlgoliaEventItem } from '~/composables/types/checkout'
import type { PriceItem } from '~/composables/types/algolia'

const fnGetMainCategory = (item: AlgoliaEventItem): string | null => {
  return item?.categories?.[0]?.name || null
}

const fnGetCategories = (item: AlgoliaEventItem): CategoryItem => {
  return item?.categories || null
}

const getPrincipalImage = (item: AlgoliaEventItem): string => {
  if (item?.main_image?.thumb) return item?.main_image?.thumb
  // Fallback thumbnails
  if (item?.media?.find((image: GalleryItem) => image?.isPrincipal)) {
    return item?.media?.find((image: GalleryItem) => image?.isPrincipal)?.thumb
  }
  return item?.media?.[0]?.thumb || ''
}

const getPrincipalAlt = (item: AlgoliaEventItem): string => {
  return item?.main_image?.alt || ''
}

const fnGetPermalink = (item: AlgoliaEventItem): string => {
  return item?.permalink || ''
}

const fnGetPrice = (item: AlgoliaEventItem, prefix = ''): string => {
  let price = item.default_price?.formatted || null

  const dates = item.dates
  if (typeof dates !== 'undefined' && dates.length > 0) {
    const date = dates[0]

    price = date.price?.formatted || null
  }

  return prefix + ' ' + price
}

const getPriceAmount = (item: AlgoliaEventItem): number =>
  parseInt(item?.default_price?.amount || '0')

/**
 * Rating levels priority:
 * 1. Event rating (level 1) - ratings specific to this event
 * 2. Partner rating (level 2) - ratings from other events by the same partner
 * 3. Google rating (level 3) - Google Business ratings for the partner
 */

type RatingLevel = {
  value: number | null | undefined
  total?: number | null | undefined
}

/**
 * Extracts rating values from an Algolia event item
 */
const extractRatingLevels = (
  event: AlgoliaEventItem
): {
  event: RatingLevel
  partner: RatingLevel
  google: RatingLevel
} => {
  return {
    event: {
      value: event?.rating ?? event?.avg_review,
      total: event?.totalRating,
    },
    partner: {
      value: event?.supplier_rating ?? event?.supplier?.data?.avg_review,
    },
    google: {
      value: event?.supplier_google_rating,
      total: event?.supplier_google_total_rating,
    },
  }
}

/**
 * Checks if a rating value is valid (not null/undefined and > 0)
 */
const isValidRating = (rating: number | null | undefined): boolean => {
  return rating != null && rating > 0
}

/**
 * Determines which rating level to use based on priority:
 * Event → Partner → Google
 */
const getRatingLevelToUse = (
  levels: ReturnType<typeof extractRatingLevels>
): 'event' | 'partner' | 'google' | null => {
  if (isValidRating(levels.event.value) && isValidRating(levels.event.total)) {
    return 'event'
  }
  if (isValidRating(levels.partner.value)) {
    return 'partner'
  }
  if (
    isValidRating(levels.google.value) &&
    isValidRating(levels.google.total)
  ) {
    return 'google'
  }
  return null
}

const getAverageRating = (
  event: AlgoliaEventItem,
  locale = 'de-DE'
): string | number | null => {
  if (isGiftcardEvent(event)) {
    return null
  }

  const levels = extractRatingLevels(event)
  const levelToUse = getRatingLevelToUse(levels)

  if (!levelToUse) {
    return 0
  }

  const ratingValue = levels[levelToUse].value
  if (!ratingValue) {
    return 0
  }

  return new Intl.NumberFormat(locale, {
    maximumSignificantDigits: 2,
    minimumSignificantDigits: 2,
  }).format(ratingValue)
}

const getBadgeLabel = (
  item: AlgoliaEventItem,
  $t: (key: string) => string
): string | undefined => {
  if (item.specialization === 'KIT') {
    return $t('product.card.productBox')
  } else if (item.has_mobile_classes) {
    return $t('product.card.mobileClasses')
  }
  return undefined
}

const getRatingsAmount = (event: AlgoliaEventItem): number => {
  const levels = extractRatingLevels(event)
  const levelToUse = getRatingLevelToUse(levels)

  if (!levelToUse) {
    return 0
  }

  switch (levelToUse) {
    case 'event':
      return levels.event.total || 0
    case 'partner':
      // Partner rating doesn't have a separate total, return event total if available
      return levels.event.total || 0
    case 'google':
      return levels.google.total || 0
    default:
      return 0
  }
}

const fnGetDuration = (item: AlgoliaEventItem): number | null => {
  /* Default duration is in minutes */
  const duration = item.default_duration
  if (duration) {
    return duration
  }

  const dates = item.dates
  if (typeof dates !== 'undefined' && dates.length > 0) {
    const date = dates[0]

    /* Duration is in minutes */
    return date.duration
  }

  return null
}
const fnGetTitle = (item: AlgoliaEventItem): string => {
  return item.title
}

const fnGetSubtitle = (item: AlgoliaEventItem): string => {
  return item.subtitle
}

const fnGetNextDateAvailableStart = (
  item: AlgoliaEventItem
): number | undefined => {
  return item?.next_event || null
}

const fnGetNextDateAvailableEnd = (
  item: AlgoliaEventItem
): number | undefined => {
  const dates = item.dates
  if (typeof dates !== 'undefined' && dates.length > 0) {
    const date = dates[0]

    return date.start_date
  }

  return undefined
}

const fnGetType = (
  item: AlgoliaEventItem
): 'HYBRID' | 'IN_PERSON' | 'ONLINE' | 'NATIONWIDE' => {
  return item.type || ''
}

const fnGetMinimumPriceAmount = (item: AlgoliaEventItem): number => {
  return item.min_price
    ? parseInt(item.min_price.amount)
    : parseInt(item.default_price.amount)
}

const fnGetMaximumPriceAmount = (item: AlgoliaEventItem): number => {
  return item.max_price && Number(item.max_price.amount) > 0
    ? parseInt(item.max_price.amount)
    : getPriceAmount(item)
}

const fnGetMinimumPriceCurrency = (item: AlgoliaEventItem): string => {
  return item.min_price ? item.min_price.currency : item.default_price.currency
}

const fnGetMaximumPriceCurrency = (item: AlgoliaEventItem): string => {
  return item.max_price ? item.max_price.currency : item.default_price.currency
}

const fnGetSupplier = (item: AlgoliaEventItem): string => {
  return item?.supplier || ''
}

const getSupplierAlias = (item: AlgoliaEventItem): string | undefined => {
  return item?.supplier_alias || undefined
}

const getSupplierId = (item: AlgoliaEventItem): string | undefined => {
  return item?.supplier_id || undefined
}

const getSupplierPermalink = (item: AlgoliaEventItem): string | undefined => {
  return item?.supplier_permalink || undefined
}

const fnGetCity = (item: AlgoliaEventItem): string => {
  return item.city || ''
}

const fnGetCitySlug = (item: AlgoliaEventItem): string => {
  return item.city_slug || ''
}

const fnGetFormattedAddress = (item: AlgoliaEventItem): string => {
  return item.address?.formatted || null
}

const fnGetFormattedZipcode = (item: AlgoliaEventItem): string => {
  return item?.address?.zipcode || ''
}

const fnGetHashedId = (item: AlgoliaEventItem): string => {
  return item?.hashed_id || item?.id || ''
}

const getId = (item: AlgoliaEventItem): string => {
  return item?.id || ''
}

const getNextDate = (item: AlgoliaEventItem): number | null => {
  const allDates = [
    ...(item?.dates_ts ?? []),
    ...(item?.dates_private_ts ?? []),
  ].sort()
  if (allDates.length === 0) return null
  const now = Date.now()
  const currentTimestampWithoutMilliseconds = ~~(now / 1000)
  const minAdvance = item?.dates?.[0].min_advance ?? 0

  for (const date of allDates) {
    if (date > currentTimestampWithoutMilliseconds + (minAdvance ?? 0) * 60) {
      return date
    }
  }

  return null
}

const isGiftcardEvent = (event: AlgoliaEventItem): boolean =>
  fnGetSupplier(event) === 'konfetti Gutscheine'

const isBoxEvent = (event: AlgoliaEventItem): boolean =>
  event?.specialization === 'KIT'

const hasShippingCosts = (event: AlgoliaEventItem): boolean =>
  event?.has_shipping_costs > 0

const fnGetLink = (
  eventItem: AlgoliaEventItem,
  queryId = null,
  addSupplierLayoutQuery = false
) => {
  let query = null

  let productType
  switch (true) {
    case isGiftcardEvent(eventItem):
      productType = 'giftcard'
      break
    case isBoxEvent(eventItem):
      productType = 'k'
      break
    default:
      productType = 'e'
      break
  }

  let link = `/${productType}/${fnGetPermalink(eventItem)}/`

  if (addSupplierLayoutQuery) {
    query = 'layout=sup'
  }

  if (queryId) {
    query = query ? `${query}&qid=${queryId}` : `qid=${queryId}`
  }

  if (query) {
    link = `${link}?${query}`
  }

  return link
}

const getRouterPushToEvent = (
  algoliaItem: AlgoliaEventItem,
  queryId: string,
  locale: string,
  addSupplierLayoutQuery = false
) => {
  let productRoute
  switch (true) {
    case isGiftcardEvent(algoliaItem):
      productRoute = 'giftcard-special'
      break
    case isBoxEvent(algoliaItem):
      productRoute = 'k-boxPermalink'
      break
    default:
      productRoute = 'e-eventPermalink'
      break
  }
  return {
    name: `${productRoute}___${locale}`,
    query: {
      ...(queryId && { qid: queryId }),
      ...(addSupplierLayoutQuery && { layout: 'sup' }),
    },
    params: {
      special: fnGetPermalink(algoliaItem),
      eventPermalink: fnGetPermalink(algoliaItem),
      boxPermalink: fnGetPermalink(algoliaItem),
      algoliaEventFromPreviousPage: algoliaItem,
    },
  }
}

const getItemsForGtm = (itemList: AlgoliaEventItem): GTMAlgoliaEventItem[] => {
  const length = itemList.length
  const items: GTMAlgoliaEventItem[] = []

  let index = 0

  for (let i = 0; i < length; i++) {
    const item = itemList[i]
    const price = getPriceAmount(item)
    items.push({
      index,
      item_id: fnGetHashedId(item),
      item_name: fnGetTitle(item),
      item_category: fnGetMainCategory(item) || '',
      item_brand: fnGetSupplier(item),
      price: parseFloat(price?.toFixed(2) || price) / 100,
      google_business_vertical: 'custom',
    })
    index++
  }

  return items
}

const getHasPromotions = (item: AlgoliaEventItem): boolean => {
  return Boolean(item?.has_promotions)
}

const getIsGroupPricing = (item: AlgoliaEventItem): boolean => {
  return Boolean(item?.is_group_pricing)
}

const getRegularPrice = (item: AlgoliaEventItem): PriceItem | undefined => {
  if (getIsGroupPricing(item) && item?.group_pricings) {
    return (
      item.group_pricings.original_price || item.group_pricings.default_price
    )
  }
  return item?.original_price || item?.default_price || 0
}

const getSpecialPrice = (item: AlgoliaEventItem): PriceItem | undefined => {
  if (!getHasPromotions(item)) return undefined
  if (getIsGroupPricing(item) && item?.group_pricings) {
    return item.group_pricings.default_price
  }
  return item?.default_price
}

const getSpecialPriceAmount = (item: AlgoliaEventItem): number | undefined => {
  if (!item) return undefined
  return parseInt(getSpecialPrice(item)?.amount || '0')
}

const getRegularPriceAmount = (item: AlgoliaEventItem): number | undefined => {
  if (!item) return undefined
  return parseInt(getRegularPrice(item)?.amount || '0')
}

const getIsTeamevent = (item: AlgoliaEventItem): boolean => {
  if (!item) return false
  return Boolean(item?.team_event_only) || false
}

const getIsPartnerRating = (event: AlgoliaEventItem): boolean => {
  if (isGiftcardEvent(event)) {
    return false
  }

  const levels = extractRatingLevels(event)
  const hasEventRating =
    isValidRating(levels.event.value) && isValidRating(levels.event.total)
  const hasPartnerRating = isValidRating(levels.partner.value)

  // It's a partner rating if event rating is missing but partner rating exists
  return !hasEventRating && hasPartnerRating
}

const getIsThirdPartyRating = (event: AlgoliaEventItem): boolean => {
  if (isGiftcardEvent(event)) {
    return false
  }

  const levels = extractRatingLevels(event)
  const hasEventRating =
    isValidRating(levels.event.value) && isValidRating(levels.event.total)
  const hasPartnerRating = isValidRating(levels.partner.value)
  const hasGoogleRating =
    isValidRating(levels.google.value) && isValidRating(levels.google.total)

  // It's a third-party rating only if event and partner ratings are missing
  // but Google rating exists
  return !hasEventRating && !hasPartnerRating && hasGoogleRating
}

const getReviewsType = (
  event: AlgoliaEventItem
): 'event' | 'partner' | 'third-party' => {
  if (isGiftcardEvent(event)) {
    return 'event'
  }

  const levels = extractRatingLevels(event)
  const levelToUse = getRatingLevelToUse(levels)

  switch (levelToUse) {
    case 'event':
      return 'event'
    case 'partner':
      return 'partner'
    case 'google':
      return 'third-party'
    default:
      // Default to 'event' if no ratings found
      return 'event'
  }
}

export const algoliaEventGetters = {
  getPrincipalImage,
  getPrincipalAlt,
  fnGetMainCategory,
  fnGetCategories,
  fnGetPermalink,
  fnGetPrice,
  getPriceAmount,
  getAverageRating,
  getRatingsAmount,
  fnGetDuration,
  fnGetTitle,
  fnGetSubtitle,
  getBadgeLabel,
  fnGetNextDateAvailableStart,
  fnGetNextDateAvailableEnd,
  getNextDate,
  fnGetType,
  fnGetMinimumPriceAmount,
  fnGetMinimumPriceCurrency,
  fnGetSupplier,
  fnGetCity,
  fnGetCitySlug,
  fnGetFormattedAddress,
  fnGetFormattedZipcode,
  fnGetMaximumPriceAmount,
  fnGetMaximumPriceCurrency,
  fnGetHashedId,
  getId,
  fnGetLink,
  isGiftcardEvent,
  isBoxEvent,
  hasShippingCosts,
  getRouterPushToEvent,
  getSupplierPermalink,
  getSupplierAlias,
  getSupplierId,
  getItemsForGtm,
  getRegularPrice,
  getSpecialPrice,
  getRegularPriceAmount,
  getSpecialPriceAmount,
  getHasPromotions,
  getIsGroupPricing,
  getIsTeamevent,
  getIsPartnerRating,
  getIsThirdPartyRating,
  getReviewsType,
}
